import {tx, ptx, usePageText} from '../lib/page-text';
import { RequestFeedback } from '../components/RequestFeedback';
import { useAuth } from '../lib/auth-store';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { Modal, EmptyState, Code } from '../components/ui';
import { WebhookIcon, PlusIcon } from '../components/icons';

const EVENTS = [
  ...['reminder','followup','registration','attended','quiz','checkin','card','material','booking','completion'].map(kind => ({id:`live.${kind}`,desc:`Event ${kind} when Follow-up is enabled for the stream.`})),
  { id: 'stream.started',        desc: 'Fires when a stream starts publishing (RTMP / WebRTC).' },
  { id: 'stream.ended',          desc: 'Fires when a stream stops publishing or disconnects.' },
  { id: 'stream.failed',         desc: 'Fires when a publish attempt fails (auth, codec, etc.).' },
  { id: 'recording.ready',       desc: 'Fires when a VOD recording finishes processing.' },
  { id: 'recording.failed',      desc: 'Fires when recording processing fails.' },
  { id: 'transcode.ready',       desc: 'Fires when a transcode job emits all renditions.' },
  { id: 'transcode.failed',      desc: 'Fires when a transcode job fails.' },
  { id: 'package.ready',         desc: 'Fires when HLS / DASH manifests are written.' },
  { id: 'package.failed',        desc: 'Fires when packaging fails.' },
  { id: 'transcription.ready',   desc: 'Fires for each finalized caption window.' },
  { id: 'transcription.failed',  desc: 'Fires when transcription fails.' },
];

export function WebhooksPage() {
  usePageText();
  const canManage=useAuth(state=>['OWNER','ADMIN'].includes(state.user?.role??''));
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showSecret, setShowSecret] = useState<{ url: string; secret: string } | null>(null);
  const [openDeliveriesFor, setOpenDeliveriesFor] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['webhooks'], queryFn: async () => (await api.get('/me/webhooks')).data });
  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/me/webhooks/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const toggle=useMutation({mutationFn:async(w:any)=>(await api.patch(`/me/webhooks/${w.id}`,{active:!w.active})).data,onSuccess:()=>qc.invalidateQueries({queryKey:['webhooks']})});
  return (
    <div>
      <div className="row-between" style={{ marginBottom: 28,flexWrap:'wrap',gap:12 }}>
        <div>
          <h1 className="h1">{tx("Webhooks")}</h1>
          <p className="subtitle" style={{ marginBottom: 0 }}>{tx("Get notified when your streams start, end, fail, or finish recording")}</p>
        </div>
        <button className="btn lg" disabled={!canManage} onClick={() => setShowCreate(true)}>{ptx("{v0} New endpoint", {v0: (<PlusIcon size={16}/>)})}</button>
      </div>

      <p className="muted">{tx("Deliveries are signed and retried up to three times. Receivers should deduplicate X-Kardolive-Delivery. Only public HTTPS endpoints are supported; redirects are refused.")}</p>
      <RequestFeedback pending={list.isPending} failed={list.isError} hasData={!!list.data} retry={()=>void list.refetch()}/>
      {(del.isError||toggle.isError)&&<p role="alert" className="error">{(del.error as any)?.response?.data?.message??(toggle.error as any)?.response?.data?.message??tx("Could not update endpoint. Try again.")}</p>}
      {list.data && list.data.length === 0 ? (
        <EmptyState
          icon={<WebhookIcon/>}
          title={tx("No webhook endpoints")}
          body={tx("Add an HTTPS endpoint to receive POST notifications when stream events happen. We'll sign every request with HMAC-SHA256.")}
          action={<button className="btn" disabled={!canManage} onClick={() => setShowCreate(true)}>{ptx("{v0} Register endpoint", {v0: (<PlusIcon/>)})}</button>}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {list.data?.map((w: any) => (
            <div key={w.id} className="card">
              <div className="row-between" style={{flexWrap:'wrap',gap:16}}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="mono" style={{ fontSize: 13.5, fontWeight: 500, wordBreak: 'break-all' }}>{w.url}</div>
                  <div className="flex gap" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                    {w.events.map((e: string) => <span key={e} className="badge mono" style={{ fontSize: 11 }}>{e}</span>)}
                  </div>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
                    <span className="badge">{w.active?tx("Active"):tx("Paused")}</span> · {w._count.deliveries}{tx(" deliveries · created ")}{new Date(w.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap" style={{flexWrap:'wrap'}}>
                  <button className="btn outline sm" onClick={() => setOpenDeliveriesFor(openDeliveriesFor === w.id ? null : w.id)}>
                    {openDeliveriesFor === w.id ? tx("Hide deliveries") : tx("View deliveries")}
                  </button>
                  <button className="btn outline sm" disabled={!canManage||!w.secret} onClick={() => setShowSecret({ url: w.url, secret: w.secret })}>{tx("Show secret")}</button>
                  {canManage&&<button className="btn outline sm" disabled={toggle.isPending} onClick={()=>toggle.mutate(w)}>{w.active?tx("Pause endpoint"):tx("Resume endpoint")}</button>}
                  {canManage&&<button className="btn outline sm" disabled={del.isPending} onClick={() => { if (confirm(`Delete webhook ${w.url} and its delivery history? Pause it instead to keep history.`)) del.mutate(w.id); }}>{tx("Delete")}</button>}
                </div>
              </div>
              {openDeliveriesFor === w.id && <DeliveriesPanel endpointId={w.id} canManage={canManage} active={w.active}/>}
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateWebhookModal onClose={() => setShowCreate(false)} onCreated={(w) => { setShowCreate(false); setShowSecret({ url: w.url, secret: w.secret }); qc.invalidateQueries({ queryKey: ['webhooks'] }); }}/>}
      {showSecret && <SecretModal url={showSecret.url} secret={showSecret.secret} onClose={() => setShowSecret(null)}/>}
    </div>
  );
}

function DeliveriesPanel({ endpointId,canManage,active }: { endpointId: string;canManage:boolean;active:boolean }) {
  usePageText();
  const qc = useQueryClient();
  const dq = useQuery({
    queryKey: ['deliveries', endpointId],
    queryFn: async () => (await api.get(`/me/webhooks/${endpointId}/deliveries`)).data as any[],
    refetchInterval: 5_000,
  });
  const retry = useMutation({
    mutationFn: async (id: string) => (await api.post(`/me/webhooks/deliveries/${id}/retry`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries', endpointId] }),
  });

  if (dq.isLoading) return <div className="muted" style={{ marginTop: 12, fontSize: 12 }}>{tx("Loading deliveries…")}</div>;
  if(dq.isError&&!dq.data)return <RequestFeedback pending={false} failed retry={()=>void dq.refetch()}/>;
  if (!dq.data || dq.data.length === 0) {
    return <div className="muted" style={{ marginTop: 12, fontSize: 12 }}>{tx("No deliveries yet — they appear once you publish events.")}</div>;
  }

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <RequestFeedback pending={false} failed={dq.isError} hasData retry={()=>void dq.refetch()}/>
      {retry.isError&&<p role="alert" className="error">{(retry.error as any)?.response?.data?.message??tx("Retry could not be queued. Try again.")}</p>}
      <p className="muted">{tx("Latest 100 deliveries. Pausing stops future attempts; it cannot recall a request already sent. After resuming, retry failed deliveries individually.")}</p>
      <div className="dashboard-table"><table style={{ width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--text-mute)' }}>
            <th style={{ padding: '4px 8px' }}>{tx("Event")}</th>
            <th style={{ padding: '4px 8px' }}>{tx("Status")}</th>
            <th style={{ padding: '4px 8px' }}>{tx("Attempts")}</th>
            <th style={{ padding: '4px 8px' }}>{tx("HTTP")}</th>
            <th style={{ padding: '4px 8px' }}>{tx("When")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {dq.data.map((d) => {
            const succeeded = !!d.succeededAt;
            const failed = !!d.failedAt;
            const pending = !succeeded && !failed;
            const tone = succeeded ? 'var(--success)' : failed ? 'var(--danger)' : '#f5a524';
            return (
              <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td className="mono" style={{ padding: '6px 8px' }}>{d.event}</td>
                <td style={{ padding: '6px 8px', color: tone, fontWeight: 600 }}>
                  {succeeded ? tx("Delivered") : failed ? tx("Failed") : d.recoverable ? tx("Recovery available") : tx("Queued / sending")}
                  {failed&&d.errorCode&&<div className="muted" style={{fontWeight:400,fontSize:11}}>{d.errorCode==='endpoint_or_event_paused'?tx("Endpoint, workspace or event paused"):d.errorCode==='endpoint_unavailable'?tx("Could not reach a public HTTPS receiver"):d.errorCode==='redirect_refused'?tx("Redirect refused"):d.errorCode==='attempts_exhausted'?tx("Attempts exhausted after interruption"):tx("Receiver returned an error")}</div>}
                </td>
                <td style={{ padding: '6px 8px' }}>{d.attempts}</td>
                <td style={{ padding: '6px 8px' }}>{d.responseCode ?? '—'}</td>
                <td className="muted" style={{ padding: '6px 8px' }}>{new Date(d.createdAt).toLocaleString()}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {(failed||d.recoverable) && canManage && (
                    <button className="btn outline sm" disabled={retry.isPending||!active} onClick={() => retry.mutate(d.id)}>
                      {retry.isPending ? tx("Queuing…") : d.recoverable ? tx("Recover delivery") : tx("Retry")}
                    </button>
                  )}
                  {pending && !d.recoverable && <span className="muted" style={{ fontSize: 11 }}>{tx("Awaiting result")}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
    </div>
  );
}

function CreateWebhookModal({ onClose, onCreated }: { onClose: () => void; onCreated: (w: { url: string; secret: string }) => void }) {
  usePageText();
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['stream.started', 'stream.ended']);
  const [err, setErr] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async () => (await api.post('/me/webhooks', { url, events })).data,
    onSuccess: (data) => onCreated(data),
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create'),
  });

  function toggle(ev: string) {
    setEvents((cur) => cur.includes(ev) ? cur.filter((s) => s !== ev) : [...cur, ev]);
  }

  return (
    <Modal onClose={onClose}>
      <h2>{tx("New webhook endpoint")}</h2>
      <p className="modal-subtitle">{tx("We'll POST a JSON payload to this URL. Signature in X-Kardolive-Signature header (HMAC-SHA256).")}</p>
      <label className="label" htmlFor="webhook-url">{tx("Endpoint URL")}</label>
      <input id="webhook-url" className="input mono" autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-app.com/hooks/kardolive" type="url"/>
      <div style={{ height: 18 }}/>
      <label className="label">{tx("Events to subscribe")}</label>
      <div className="card" style={{ padding: '4px 16px' }}>
        {EVENTS.map((e) => (
          <div key={e.id} className="toggle-row">
            <div className="label-block">
              <span className="name mono" style={{ fontFamily: 'ui-monospace, monospace' }}>{e.id}</span>
              <span className="desc">{e.desc}</span>
            </div>
            <button type="button" role="switch" aria-label={e.id} aria-checked={events.includes(e.id)} className={`toggle ${events.includes(e.id) ? 'on' : ''}`} onClick={() => toggle(e.id)}/>
          </div>
        ))}
      </div>
      {err && <div className="error">{err}</div>}
      <div className="row-between" style={{ marginTop: 20 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!url || events.length === 0 || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? tx("Creating…") : tx("Create endpoint")}
        </button>
      </div>
    </Modal>
  );
}

function SecretModal({ url, secret, onClose }: { url: string; secret: string; onClose: () => void }) {
  usePageText();
  return (
    <Modal onClose={onClose}>
      <h2>{tx("Webhook signing secret")}</h2>
      <p className="modal-subtitle">{url}</p>
      <label className="label">{tx("HMAC signing secret")}</label>
      <Code value={secret} large/>
      <p className="muted" style={{ fontSize: 12, marginTop: 16, marginBottom: 8 }}>{tx("Verify in Node:")}</p>
      <pre className="code" style={{ fontSize: 12 }}>{`import { createHmac, timingSafeEqual } from 'crypto';

function verify(rawBody, signature, secret) {
  if (!/^sha256=[a-f0-9]{64}$/i.test(signature ?? '')) return false;
  const expected = createHmac('sha256', secret).update(rawBody).digest();
  const received = Buffer.from(signature.slice(7), 'hex');
  return received.length === expected.length && timingSafeEqual(expected, received);
}`}</pre>
      <p className="muted">{tx("Pass the full secret from private server configuration and the exact raw request body. Store successful X-Kardolive-Delivery identifiers to ignore duplicate processing. Never expose the secret in browser code.")}</p>
      <div className="row-between" style={{ marginTop: 20 }}>
        <span/>
        <button className="btn" onClick={onClose}>{tx("Close")}</button>
      </div>
    </Modal>
  );
}
